/**
 * SearchManager - Handles celestial body search functionality
 * Extracted from main.ts for better organization
 */

import { AsteroidDataFetcher, AsteroidInfo } from '../data/AsteroidData';
import { UIManager } from './UIManager';
import { NewsType } from './TickerManager';

/** Searchable body types */
export type BodyType = 'sun' | 'planet' | 'moon' | 'asteroid' | 'satellite';

/** Represents a searchable celestial body */
export interface SearchableBody {
  name: string;
  type: BodyType;
  id?: string;
}

/** Callbacks for search result actions */
export interface SearchCallbacks {
  onAddNewsItem: (text: string, type: NewsType) => void;
  onAsteroidAdded: (asteroid: AsteroidInfo) => void;
  onSelectAndFocus: (name: string, type: BodyType, id?: string) => void;
  getAsteroidData: () => Map<string, AsteroidInfo>;
  getStaticAsteroidData: () => AsteroidInfo[];
}

/** Planet names in our solar system */
const PLANETS = ['Mercury', 'Venus', 'Earth', 'Mars', 'Jupiter', 'Saturn', 'Uranus', 'Neptune'];

/** Major moons rendered in the game */
const MOONS = ['Moon', 'Io', 'Europa', 'Ganymede', 'Callisto'];

/**
 * SearchManager provides body search with autocomplete
 */
export class SearchManager {
  private ui: UIManager;
  private callbacks: SearchCallbacks;
  private searchableBodies: SearchableBody[] = [];
  private selectedIndex = -1;
  
  constructor(ui: UIManager, callbacks: SearchCallbacks) {
    this.ui = ui;
    this.callbacks = callbacks;
  }
  
  /**
   * Initialize searchable bodies list from static data
   */
  init(staticAsteroidData: AsteroidInfo[]): void {
    // Clear existing
    this.searchableBodies = [];
    
    // Add Sun
    this.searchableBodies.push({ name: 'Sun', type: 'sun' });

    // Add planets
    PLANETS.forEach(name => {
      this.searchableBodies.push({ name, type: 'planet' });
    });

    // Add moons
    MOONS.forEach(name => {
      this.searchableBodies.push({ name, type: 'moon' });
    });

    // Add ISS
    this.searchableBodies.push({ name: 'ISS', type: 'satellite' });

    // Add ALL asteroids from static data (not just rendered ones)
    // This allows users to search and add asteroids that aren't initially rendered
    for (const asteroid of staticAsteroidData) {
      this.searchableBodies.push({ name: asteroid.name, type: 'asteroid', id: asteroid.id });
    }
  }
  
  /**
   * Setup search input event listeners
   */
  setup(): void {
    const searchInput = this.ui.bodySearch;
    const suggestionsEl = this.ui.searchSuggestions;
    if (!searchInput || !suggestionsEl) return;

    // Handle input changes
    searchInput.addEventListener('input', () => {
      const query = searchInput.value.trim().toLowerCase();
      if (query.length === 0) {
        this.hideSuggestions();
        return;
      }
      this.showSuggestions(query);
    });

    // Handle keyboard navigation
    searchInput.addEventListener('keydown', async (e) => {
      const suggestions = suggestionsEl.querySelectorAll('.search-suggestion');
      
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, suggestions.length - 1);
        this.updateSelection();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
        this.updateSelection();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (suggestions.length > 0) {
          // Select from existing suggestions
          const index = this.selectedIndex >= 0 ? this.selectedIndex : 0;
          const selected = suggestions[index] as HTMLElement;
          if (selected) {
            this.selectResult(selected.dataset.name!, selected.dataset.type as BodyType, selected.dataset.id);
          }
        } else {
          // No matches - try to fetch from NASA API
          const query = searchInput.value.trim();
          if (query.length > 0) {
            await this.searchAndAddAsteroid(query);
          }
        }
      } else if (e.key === 'Escape') {
        this.hideSuggestions();
        searchInput.blur();
      }
    });

    // Hide suggestions when clicking outside
    document.addEventListener('click', (e) => {
      if (!searchInput.contains(e.target as Node) && !suggestionsEl.contains(e.target as Node)) {
        this.hideSuggestions();
      }
    });

    // Show suggestions when focusing on non-empty input
    searchInput.addEventListener('focus', () => {
      const query = searchInput.value.trim().toLowerCase();
      if (query.length > 0) {
        this.showSuggestions(query);
      }
    });
  }
  
  /**
   * Add an asteroid to searchable bodies (for dynamically added asteroids)
   */
  addAsteroid(asteroid: AsteroidInfo): void {
    // Check if already in list
    if (this.searchableBodies.some(b => b.type === 'asteroid' && b.id === asteroid.id)) {
      return;
    }
    this.searchableBodies.push({
      name: asteroid.name,
      type: 'asteroid',
      id: asteroid.id,
    });
  }
  
  /**
   * Show search suggestions matching query
   */
  private showSuggestions(query: string): void {
    const suggestionsEl = this.ui.searchSuggestions;
    if (!suggestionsEl) return;

    // Filter and limit to 5 results
    const matches = this.searchableBodies
      .filter(body => body.name.toLowerCase().includes(query))
      .slice(0, 5);

    if (matches.length === 0) {
      this.hideSuggestions();
      return;
    }

    // Build suggestions HTML
    suggestionsEl.innerHTML = matches.map((body, index) => {
      const typeLabel = body.type.charAt(0).toUpperCase() + body.type.slice(1);
      return `<div class="search-suggestion" role="option" id="suggestion-${index}" data-name="${body.name}" data-type="${body.type}" ${body.id ? `data-id="${body.id}"` : ''}>
        ${body.name}<span class="body-type">${typeLabel}</span>
      </div>`;
    }).join('');

    // Add click handlers
    suggestionsEl.querySelectorAll('.search-suggestion').forEach(el => {
      el.addEventListener('click', () => {
        const element = el as HTMLElement;
        this.selectResult(element.dataset.name!, element.dataset.type as BodyType, element.dataset.id);
      });
    });

    suggestionsEl.classList.add('visible');
    this.selectedIndex = -1;
    
    // Update ARIA state
    const searchInput = this.ui.bodySearch;
    if (searchInput) {
      searchInput.setAttribute('aria-expanded', 'true');
    }
  }
  
  /**
   * Hide search suggestions
   */
  private hideSuggestions(): void {
    const suggestionsEl = this.ui.searchSuggestions;
    if (suggestionsEl) {
      suggestionsEl.classList.remove('visible');
      suggestionsEl.innerHTML = '';
    }
    this.selectedIndex = -1;
    
    // Update ARIA state
    const searchInput = this.ui.bodySearch;
    if (searchInput) {
      searchInput.setAttribute('aria-expanded', 'false');
      searchInput.removeAttribute('aria-activedescendant');
    }
  }
  
  /**
   * Update keyboard selection highlighting
   */
  private updateSelection(): void {
    const suggestionsEl = this.ui.searchSuggestions;
    if (!suggestionsEl) return;

    const suggestions = suggestionsEl.querySelectorAll('.search-suggestion');
    const searchInput = this.ui.bodySearch;
    
    suggestions.forEach((el, index) => {
      if (index === this.selectedIndex) {
        el.classList.add('selected');
        el.setAttribute('aria-selected', 'true');
        // Update active descendant for screen readers
        if (searchInput) {
          searchInput.setAttribute('aria-activedescendant', `suggestion-${index}`);
        }
      } else {
        el.classList.remove('selected');
        el.setAttribute('aria-selected', 'false');
      }
    });
    
    // Clear active descendant if nothing selected
    if (this.selectedIndex === -1 && searchInput) {
      searchInput.removeAttribute('aria-activedescendant');
    }
  }
  
  /**
   * Handle selection of a search result
   */
  private selectResult(name: string, type: BodyType, id?: string): void {
    // Clear search
    const searchInput = this.ui.bodySearch;
    if (searchInput) searchInput.value = '';
    this.hideSuggestions();

    // For asteroids, check if it's already rendered - if not, add it from static data
    if (type === 'asteroid' && id) {
      const asteroidData = this.callbacks.getAsteroidData();
      if (!asteroidData.has(id)) {
        // Find in static data
        const staticData = this.callbacks.getStaticAsteroidData();
        const asteroid = staticData.find(a => a.id === id);
        if (asteroid) {
          this.callbacks.onAsteroidAdded(asteroid);
          this.callbacks.onAddNewsItem(`Found and added asteroid: ${asteroid.name}`, 'market');
        }
      }
    }

    // Focus and select the object
    this.callbacks.onSelectAndFocus(name, type, id);
  }
  
  /**
   * Search NASA API and add asteroid if found
   */
  private async searchAndAddAsteroid(query: string): Promise<void> {
    const searchInput = this.ui.bodySearch;
    const suggestionsEl = this.ui.searchSuggestions;
    
    // Show loading indicator
    if (suggestionsEl) {
      suggestionsEl.innerHTML = '<div class="search-suggestion" style="color: #888;">Searching NASA database...</div>';
      suggestionsEl.classList.add('visible');
    }

    try {
      // Try to fetch from NASA API
      const asteroid = await AsteroidDataFetcher.fetchAsteroid(query);
      
      if (asteroid) {
        // Add to searchable bodies
        this.addAsteroid(asteroid);
        
        // Notify callback to add asteroid
        this.callbacks.onAsteroidAdded(asteroid);
        
        // Clear search
        if (searchInput) searchInput.value = '';
        this.hideSuggestions();
        
        // Focus on it
        this.callbacks.onSelectAndFocus(asteroid.name, 'asteroid', asteroid.id);
        
        this.callbacks.onAddNewsItem(`Found and added asteroid: ${asteroid.name}`, 'market');
      } else {
        // Show not found message
        if (suggestionsEl) {
          suggestionsEl.innerHTML = '<div class="search-suggestion" style="color: #f66;">No asteroid found with that name/number</div>';
        }
        // Auto-hide after 2 seconds
        setTimeout(() => this.hideSuggestions(), 2000);
      }
    } catch (error) {
      console.error('Error searching for asteroid:', error);
      if (suggestionsEl) {
        suggestionsEl.innerHTML = '<div class="search-suggestion" style="color: #f66;">Error searching NASA database</div>';
      }
      setTimeout(() => this.hideSuggestions(), 2000);
    }
  }
}
